// Rise of Firstborn → War and Peace migration page (/firstborn). RFB mails every player a
// one-time migration token; they log in here with their WaP account and claim it. The claim
// grants the player's reward package server-side (hmmo-backend migration-reward); the
// webshop answers {} and no item list reaches the client.

// Which States may claim is hmmo-backend's migrationRewards config, and it differs per
// environment, so the copy never names them: it points at the RFB mail, which does.

export const MIGRATION_PAGE_PATH = '/firstborn';

// Claims are expected to succeed, and the grant can take a while server-side (up to the
// backend's 30 s RPC race; web-shop gives up at 35 s), so a claim still running this long is
// welcomed without waiting for the answer. A failure that answers later takes it back.
export const CLAIM_WELCOME_AFTER_MS = 5000;

// Players type this URL from the mail, so accept any case and a trailing slash.
export function isMigrationPagePath(pathname) {
    return /^\/firstborn\/?$/i.test(pathname);
}

// Same rules as hmmo-backend migration-token.ts: drop spaces and dashes, uppercase, then
// exactly 12 hexadecimal characters. Every malformed token the server sees counts towards
// the five failed attempts an hour (then a 24-hour lockout), so refuse it here first.
export const MIGRATION_TOKEN_LENGTH = 12;

export function normalizeMigrationToken(raw) {
    return (raw ?? '').replace(/[\s-]/g, '').toUpperCase();
}

export function isValidMigrationToken(token) {
    return /^[A-F0-9]{12}$/.test(token);
}

export function hasNonHexCharacters(token) {
    return /[^A-F0-9]/.test(token);
}

// player-state carries migrationRewardsStatus once the backend half is deployed; until
// then (field absent) the claim must not be presented as ready.
const VIEWS = new Set(['can_claim', 'claimed', 'wrong_kingdom']);

export function migrationView(playerState) {
    const status = playerState?.migrationRewardsStatus;
    return VIEWS.has(status) ? status : 'not_open';
}

// web-shop's claim-migration-rewards failures, as { key, refreshStatus }: the copy to show,
// and whether player-state must be re-read first (the account may have moved on).
const REFUSALS = {
    'Invalid migration token': { key: 'RfbErrorInvalidToken', refreshStatus: false },
    'Invalid user': { key: 'RfbErrorInvalidUser', refreshStatus: false },
    'This migration token has already been claimed': { key: 'RfbErrorTokenAlreadyClaimed', refreshStatus: false },
    'You have already claimed migration rewards': { key: 'RfbErrorAccountAlreadyClaimed', refreshStatus: true },
    'You must be in one of the migration kingdoms to claim migration rewards': { key: 'RfbErrorWrongKingdom', refreshStatus: true },
    'Migration rewards are not ready yet': { key: 'RfbErrorRewardNotReady', refreshStatus: false },
    'Too many failed migration attempts. Please try again in 24 hours': { key: 'RfbErrorRateLimited', refreshStatus: false },
};

const GENERIC = { key: 'PleaseTryAgainLater', refreshStatus: false };

export function claimFailure(status, message) {
    if (status === 503) return { key: 'RfbErrorUnavailable', refreshStatus: false };
    // The claim may still commit after the timeout, so look before letting them retry.
    if (status === 504) return { key: 'RfbErrorTimeout', refreshStatus: true };
    const refusal = REFUSALS[(message ?? '').replace(/^Error:\s*/, '')];
    return status === 400 && refusal ? refusal : GENERIC;
}

// English copy used until the keys land in the S3 language files: pass as
// $_(key, { default: RFB_COPY[key] }).
export const RFB_COPY = {
    RfbWelcomeTitle: 'Welcome, Firstborn!',
    RfbWelcomeText: 'Rise of Firstborn is closing its gates, but your story goes on. Bring your legacy to War and Peace and claim the rewards we have prepared for you.',
    RfbStepsTitle: 'How to claim',
    RfbStepAccount: 'Create a War and Peace account in the game.',
    RfbStepKingdom: 'Move your city to one of the States named in your Rise of Firstborn mail.',
    RfbStepToken: 'Enter the migration token from your Rise of Firstborn mail below.',
    RfbTokenPlaceholder: 'Migration token',
    RfbTokenHint: 'The 12 characters from your Rise of Firstborn mail. Dashes, spaces and small letters are fine.',
    RfbTokenBadCharacters: 'Migration tokens use only the digits 0-9 and the letters A-F. Check for an O that should be a 0.',
    RfbClaim: 'Claim rewards',
    RfbClaiming: 'Claiming…',
    RfbClaimingFor: 'Rewards go to {player}. A token can be claimed by one War and Peace account only.',
    RfbNotYou: 'Not you? Log out',
    RfbSuccessTitle: 'Welcome to War and Peace!',
    RfbSuccessText: 'Your Firstborn rewards have been applied to your account.',
    RfbClaimedTitle: 'Rewards claimed',
    RfbClaimedText: 'This account has already claimed its Firstborn rewards.',
    RfbGoToSupplies: 'Go to supplies',
    RfbFinishingClaim: 'Finishing your claim…',
    RfbWrongKingdomTitle: 'Your city is not in the right State yet',
    RfbWrongKingdomText: 'Move it in the game to one of the States named in your Rise of Firstborn mail, then check again.',
    RfbNotOpenTitle: 'Claiming opens soon',
    RfbNotOpenText: 'Firstborn rewards cannot be claimed yet. Keep your migration token and come back soon.',
    RfbStatusErrorText: 'We could not check your account. Please try again.',
    RfbCheckAgain: 'Check again',
    RfbLoginNotice: 'Log in with your War and Peace player name and login token first. Your Rise of Firstborn migration token is not your login token: you enter it after logging in.',
    RfbErrorInvalidToken: 'We could not find that token. Check it against your Rise of Firstborn mail: after 5 failed tries, claiming is locked for 24 hours.',
    RfbErrorInvalidUser: 'We could not verify your account. Refresh the page and try again, and contact support if it keeps happening.',
    RfbErrorTokenAlreadyClaimed: 'This token has already been claimed.',
    RfbErrorAccountAlreadyClaimed: 'This War and Peace account has already claimed its Firstborn rewards.',
    RfbErrorWrongKingdom: 'Move your city to one of the States named in your Rise of Firstborn mail, then try again.',
    RfbErrorRewardNotReady: 'Your Firstborn rewards are still being prepared. Please try again later.',
    RfbErrorRateLimited: 'Too many failed attempts. You can try again in 24 hours.',
    RfbErrorUnavailable: 'Claiming is unavailable right now. Please try again in a few minutes.',
    RfbErrorTimeout: 'Your claim took too long to confirm and has not shown up on your account yet. Wait a minute, then check again before trying once more.',
};
